import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, HelpCircle, XCircle, Send, MessageSquare, Eye } from "lucide-react";
import { setApplicationStatus, errMsg } from "@/lib/applyApi";
import { approveListing, requestChanges } from "@/lib/api";
import { AdvertisingTermsPicker, validFee } from "@/components/AdvertisingTermsPicker";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const btn = "h-11 rounded-full text-sm";

function Ask({ label, placeholder, required, onSubmit, onCancel, children }) {
  const [t, setT] = useState("");
  return (
    <div data-testid="pipeline-action-form" className="space-y-2 rounded-xl border border-stone-200 p-3">
      <p className="text-sm font-semibold">{label}</p>
      {children}
      {placeholder && <Textarea data-testid="pipeline-action-message" rows={3} value={t} onChange={(e) => setT(e.target.value)} placeholder={placeholder} className="text-base sm:text-sm" />}
      <div className="flex gap-2">
        <Button variant="outline" data-testid="pipeline-action-cancel" onClick={onCancel} className={`${btn} flex-1`}>Cancel</Button>
        <Button data-testid="pipeline-action-confirm" disabled={required && !t.trim()} onClick={() => onSubmit(t.trim())} className={`${btn} flex-1`}>Confirm</Button>
      </div>
    </div>
  );
}

// Every action calls the EXISTING endpoint used by Applications / Review Queue.
export function StageActions({ c, onDone, onLeave, onReview, inAdBuilder = false, adDirty = false }) {
  const [ask, setAsk] = useState(null);
  const [fee, setFee] = useState({ fee: "10" });
  const run = async (fn, ok) => {
    try { const r = await fn(); toast.success(typeof ok === "function" ? ok(r) : ok); setAsk(null); onDone(); }
    catch (e) { toast.error(errMsg(e, "Action failed")); }
  };
  const appId = c.ids.application_id, hid = c.ids.horse_id;
  if (ask === "approve") return (
    <Ask label="Approve application and set agreement terms" onCancel={() => setAsk(null)}
      onSubmit={() => validFee(fee.fee) && run(() => setApplicationStatus(appId, "approved", "", { advertising_fee_percent: Number(fee.fee) }), "Application approved")}>
      <AdvertisingTermsPicker fee={fee.fee} onChange={setFee} autoDefault />
    </Ask>
  );
  if (ask === "info") return <Ask label="Request more information" placeholder="What do you need from the seller?" required onCancel={() => setAsk(null)} onSubmit={(m) => run(() => setApplicationStatus(appId, "more_info_requested", m), "Information requested")} />;
  if (ask === "decline") return <Ask label="Decline application" placeholder="Reason (optional)" onCancel={() => setAsk(null)} onSubmit={(m) => run(() => setApplicationStatus(appId, "declined", m), "Application declined")} />;
  if (ask === "changes") return <Ask label="Request ad changes" placeholder="Note for the seller" required onCancel={() => setAsk(null)} onSubmit={(m) => run(() => requestChanges(hid, m), "Changes requested")} />;

  const acts = [];
  if (c.stage === "approvals" && c.kind === "application") {
    acts.push(["approve", "Approve", CheckCircle2, () => setAsk("approve"), "bg-primary hover:bg-amber-900 text-white border-transparent"]);
    if (c.application_status !== "more_info_requested") acts.push(["info", "Request Info", HelpCircle, () => setAsk("info"), ""]);
    acts.push(["decline", "Decline", XCircle, () => setAsk("decline"), "text-red-700 border-red-300"]);
  }
  const review = hid && c.listing_status === "submitted";
  if (review) {
    acts.push(["approve-ad", "Approve Ad", CheckCircle2, () => (adDirty ? toast.error("Save your ad changes before approving.") : run(() => approveListing(hid), "Ad approved — moved to Ready to Post")), "bg-primary hover:bg-amber-900 text-white border-transparent"]);
    acts.push(["changes", "Request Changes", MessageSquare, () => setAsk("changes"), ""]);
  }
  if (c.stage === "ready" && hid && !inAdBuilder && ["approved", "scheduled", "unpublished"].includes(c.listing_status)) {
    // Publishing lives in Ad Details (one shared publish dialog).
    acts.push(["publish", "Publish Ad…", Send, () => onReview?.(), "bg-primary hover:bg-amber-900 text-white border-transparent"]);
  }
  if (!acts.length) return null;
  return (
    <div className="space-y-2">
      {review && (
        <div data-testid="pipeline-ad-review-banner" className="rounded-xl border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900">
          <p className="font-semibold flex items-center gap-1.5"><Eye className="h-4 w-4" />Ad submitted — review required</p>
          <p className="mt-0.5 text-xs">The seller submitted the completed Ad Template. Review it{inAdBuilder ? " below" : ""}, then approve it or request changes. Nothing is approved automatically. Approving does not post to Facebook or publish to the website.</p>
          {adDirty && <p data-testid="pipeline-ad-unsaved" className="mt-1 text-xs font-semibold text-orange-800">You have unsaved ad changes — save before approving.</p>}
          {!inAdBuilder && <Button data-testid="pipeline-review-ad" size="sm" variant="outline" onClick={onReview} className="mt-2 rounded-full bg-white">Review Ad</Button>}
        </div>
      )}
      <div data-testid="pipeline-stage-actions" className="grid grid-cols-2 gap-2">
        {acts.map(([k, l, Icon, on, tone]) => (
          <Button key={k} variant="outline" data-testid={`pipeline-act-${k}`} onClick={on} className={`${btn} ${tone}`}><Icon className="h-4 w-4 mr-1.5" />{l}</Button>
        ))}
      </div>
    </div>
  );
}
